import React, { useMemo } from 'react';
import { Gem, Search } from 'lucide-react';

/**
 * Top-left tag pills overlay shown on the EXPANDED map so the guest
 * can see + dismiss active filters without collapsing the map. Sits
 * left of the top-right Minimize button.
 *
 * Parent owns the tag state and passes remove callbacks per kind so
 * this stays a pure render.
 */
export function MapSelectedTagsOverlay({
  selectedVenueTags,
  selectedExperienceTags,
  onRemoveVenueTag,
  onRemoveExperienceTag,
}) {
  const allSelected = useMemo(() => [
    ...selectedVenueTags.map((t) => ({ tag: t, kind: 'venue' })),
    ...selectedExperienceTags.map((t) => ({ tag: t, kind: 'experience' })),
  ], [selectedVenueTags, selectedExperienceTags]);

  if (allSelected.length === 0) return null;

  return (
    <div
      data-testid="map-selected-tags-overlay"
      style={{
        position: 'absolute',
        top: 16,
        left: 16,
        display: 'flex',
        flexWrap: 'wrap',
        gap: 6,
        maxWidth: 'calc(100% - 140px)', // leaves room for the Minimize pill on the right
        zIndex: 1000,
      }}
    >
      {allSelected.map(({ tag, kind }) => (
        <span
          key={`${kind}-${tag}`}
          data-testid={`map-selected-tag-pill-${kind}-${tag.replace(/[^a-z0-9]+/g, '-')}`}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '5px 6px 5px 10px',
            borderRadius: 999,
            background: kind === 'experience'
              ? 'linear-gradient(135deg, rgba(124,58,237,0.9), rgba(79,70,229,0.9))'
              : 'rgba(10, 10, 16, 0.55)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            border: '1px solid rgba(255, 255, 255, 0.22)',
            color: '#FFFFFF',
            fontFamily: "'Inria Serif', serif",
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: '0.01em',
            maxWidth: 180,
          }}
        >
          {kind === 'experience'
            ? <Gem size={11} strokeWidth={2.4} color="#FFFFFF" />
            : <Search size={11} strokeWidth={2.4} color="#FFFFFF" />}
          <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{tag}</span>
          <button
            type="button"
            onClick={() => {
              if (kind === 'venue') onRemoveVenueTag(tag);
              else onRemoveExperienceTag(tag);
            }}
            aria-label={`Remove ${tag}`}
            data-testid={`map-selected-tag-pill-remove-${kind}-${tag.replace(/[^a-z0-9]+/g, '-')}`}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 18,
              height: 18,
              borderRadius: 999,
              border: 'none',
              background: 'rgba(255, 255, 255, 0.22)',
              color: '#FFFFFF',
              cursor: 'pointer',
              padding: 0,
            }}
          >
            <span aria-hidden style={{ fontSize: 11, lineHeight: 1, fontWeight: 800 }}>✕</span>
          </button>
        </span>
      ))}
    </div>
  );
}
